import React, { useState } from 'react';
import { FileSpreadsheet } from 'lucide-react';
import { Button, RankItem, RankList, ReadOnlyValue, Toast } from '../../components/ui';
import ExtractedBomModal, { ExtractedBomRow } from '../../components/bom/ExtractedBomModal';
import { BomSaveFailure } from '../../components/bom/extracted/types';
import LocationAutocomplete from '../../components/common/LocationAutocomplete';
import { ManualEditRequest } from '../../types/bomVersions';
import Section from './Section';
import BomProposalGallery from './BomProposalGallery';

/**
 * /design — Step 2 Sourcing Criteria pieces next to the wizard criteria
 * mock: the Extracted BOM strip (populated + empty), the "What matters
 * most" rank card, the optional "Source suppliers from" field in all three
 * of its states, and the extracted-BOM sheet — editable (edit, add, remove,
 * save with a refusal on a line called "fail") and as a viewer reads it,
 * then the proposal card Robbie answers "Enhance with Robbie" with.
 */

// Same restriction the wizard uses — regions, not street addresses.
const SOURCING_PLACE_TYPES = ['(regions)'];

const PRIORITIES: RankItem[] = [
  { id: 'quality', label: 'Quality', description: 'Product quality and reliability' },
  { id: 'price', label: 'Price', description: 'Cost competitiveness' },
  { id: 'reliability', label: 'Reliability', description: 'Supplier track record' },
  { id: 'established', label: 'Established Company', description: 'Reputation and stability' },
  { id: 'support', label: 'Support', description: 'Service and technical support' },
  { id: 'warranty', label: 'Returns & Warranty', description: 'Return policy and coverage' },
];

const BOM_ROWS: ExtractedBomRow[] = [
  { componentId: 'C-1', name: 'Hub motor, brushless DC, 48V 200W', partNumber: 'HM-4820-BLDC', manufacturer: 'Bafang', mpn: 'G020.250.DC', description: 'Direct-drive wheel motor for AMR drive train, 200W continuous', quantity: 4, targetPrice: 5200, currency: 'INR' },
  { componentId: 'C-2', name: 'Optical encoder, 2048 PPR, ABZ', partNumber: 'ENC-2048-OPT', manufacturer: 'Broadcom', mpn: 'HEDS-5540-A11', description: 'Shaft position feedback for closed-loop speed control', quantity: 2, targetPrice: 1850, currency: 'INR' },
  { componentId: 'C-3', name: 'Motor driver, 3-phase gate, 100V', manufacturer: 'Texas Instruments', mpn: 'DRV8353RS', description: 'Gate driver stage for the BLDC hub motors', quantity: 4, targetPrice: 46.5, currency: 'USD' },
  { componentId: 'C-4', name: 'Power cable, AWG12, silicone', description: 'High-flex battery-to-driver power run', quantity: 6, targetPrice: 320, currency: 'INR' },
  { componentId: 'C-5', name: 'Deep-groove ball bearing, sealed', partNumber: 'BRG-6204-2RS', description: 'Sealed bearing for drive shaft, 20mm bore' },
];

// A long BOM, to check that typing in one cell stays instant.
const LONG_BOM: ExtractedBomRow[] = Array.from({ length: 300 }, (_, i) => ({
  componentId: `L-${i + 1}`,
  name: `Fastener kit ${i + 1}`,
  partNumber: i % 7 === 0 ? undefined : `FK-${String(i + 1).padStart(4, '0')}`,
  mpn: i % 3 === 0 ? `M${8000 + i}` : undefined,
  manufacturer: i % 3 === 0 ? 'Würth' : undefined,
  description: 'Stainless M4 socket-head screws with washers',
  quantity: 10 + (i % 40),
  targetPrice: 12 + (i % 9),
  currency: 'INR',
}));

const BOM_COMPLETENESS = {
  total: 5,
  incomplete: [
    { componentId: 'C-4', missing: ['partNumber' as const] },
    { componentId: 'C-5', missing: ['quantity' as const] },
  ],
};

const BomStrip: React.FC<{ count: number; onOpen?: () => void }> = ({ count, onOpen }) => (
  <div className="flex items-center gap-s6 rounded-card border border-line bg-surface px-s6 py-[12px]">
    <span className="flex items-center gap-s3">
      <FileSpreadsheet className="h-[15px] w-[15px] shrink-0 text-success-ink" />
      <span className="text-row-title text-ink">Extracted BOM</span>
    </span>
    <span className="font-mono text-mono-data text-ink-secondary">{count} components</span>
    {count > 0 ? (
      <button type="button" onClick={onOpen} className="ml-auto text-body-sm font-semibold text-action hover:text-action-deep">
        Open BOM →
      </button>
    ) : (
      <span className="ml-auto text-meta text-ink-muted">Nothing extracted yet</span>
    )}
  </div>
);

const CriteriaGallery: React.FC = () => {
  const [rank, setRank] = useState(PRIORITIES);
  const [modalOpen, setModalOpen] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [longOpen, setLongOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState<string | null>(null);

  // Stand-in for the manual edit: a pause, then success — or the server's
  // per-line refusal when an added line is named "fail".
  const fakeSaveFor = (lineCount: number, close: () => void) => async (
    request: ManualEditRequest,
    changedCount: number
  ): Promise<BomSaveFailure | null> => {
    setSaving(true);
    await new Promise((resolve) => setTimeout(resolve, 700));
    setSaving(false);
    const failIndex = (request.adds || []).findIndex((a) => a.name.toLowerCase() === 'fail');
    if (failIndex >= 0) return { message: 'name is already on this BOM', addIndex: failIndex };
    const lines = lineCount + (request.adds?.length ?? 0) - (request.removes?.length ?? 0);
    setSaved(
      [`${lines} lines`, changedCount && `${changedCount} changed`, request.adds?.length && `${request.adds.length} added`,
        request.removes?.length && `${request.removes.length} removed`].filter(Boolean).join(', ') + '.'
    );
    close();
    return null;
  };
  const [sourcingLocation, setSourcingLocation] = useState('');

  return (
    <>
      <Section title="Step 2 — Extracted BOM strip (populated / empty)">
        <div className="flex max-w-xl flex-col gap-s4">
          <BomStrip count={BOM_ROWS.length} onOpen={() => setModalOpen(true)} />
          <BomStrip count={0} />
        </div>
      </Section>

      <Section title="Step 2 — What matters most (drag or arrow keys to rank)">
        <div className="max-w-md overflow-hidden rounded-card border border-line bg-surface">
          <div className="border-b border-line-divider-soft px-s6 py-[12px]">
            <div className="text-card-header text-ink">What matters most</div>
            <div className="mt-[2px] text-meta text-ink-muted">Drag to rank — Robbie weights suppliers by this order</div>
          </div>
          <RankList items={rank} onReorder={setRank} />
        </div>
      </Section>

      <Section title="Step 2 — Source suppliers from (optional; editable / viewer / unset)">
        <div className="flex max-w-md flex-col gap-s5 rounded-card border border-line bg-surface p-s6">
          <div>
            <label className="mb-s2 block text-meta font-semibold text-ink-secondary">
              Source suppliers from <span className="font-normal text-ink-muted">(optional)</span>
            </label>
            <LocationAutocomplete
              value={sourcingLocation}
              onChange={setSourcingLocation}
              placeholder="e.g. Coimbatore, India"
              types={SOURCING_PLACE_TYPES}
              className="h-9 w-full rounded-control border border-line-strong bg-surface pl-9 text-body-md
                text-ink placeholder:text-ink-muted transition-colors duration-100 hover:border-line-strong
                focus:border-action focus:outline-none focus:ring-2 focus:ring-action-border"
            />
            <p className="mt-s2 text-meta text-ink-muted">
              Optional — name a city, country or region and I'll search there first. Leave it blank
              and I look worldwide.
            </p>
          </div>
          <div>
            <div className="mb-s2 text-meta font-semibold text-ink-secondary">
              Viewer — a location was set
            </div>
            <ReadOnlyValue value="Coimbatore, Tamil Nadu, India" />
          </div>
          <div>
            <div className="mb-s2 text-meta font-semibold text-ink-secondary">
              Viewer — nothing set (never a dash row)
            </div>
            <ReadOnlyValue emptyLabel="Anywhere — no sourcing location set" />
          </div>
        </div>
      </Section>

      <Section title="Step 2 — Extracted BOM sheet (edit cells, add / remove lines, save)">
        <div className="flex flex-wrap gap-s4">
          <Button variant="secondary" onClick={() => setModalOpen(true)}>
            Open the BOM sheet
          </Button>
          <Button variant="secondary" onClick={() => setViewerOpen(true)}>
            Open as a viewer
          </Button>
          <Button variant="secondary" onClick={() => setLongOpen(true)}>
            Open a 300-line BOM
          </Button>
        </div>
        <p className="mt-s4 text-meta text-ink-muted">
          Save is a stand-in here (no backend). Name an added line &ldquo;fail&rdquo; to see the per-line refusal.
          In the app, Enhance with Robbie closes the sheet and asks Robbie to review every line; his answer
          arrives in the docked panel with the proposal card below.
        </p>
        {saved && (
          <Toast status="success" title="Saved." message={saved} onDismiss={() => setSaved(null)} className="mt-s4" />
        )}
        <ExtractedBomModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          components={BOM_ROWS}
          completeness={BOM_COMPLETENESS}
          fileName="AMR-Drive-BOM-v3.xlsx"
          saving={saving}
          onSave={fakeSaveFor(BOM_ROWS.length, () => setModalOpen(false))}
          onEnhanceWithRobbie={() => setModalOpen(false)}
        />
        <ExtractedBomModal
          open={longOpen}
          onClose={() => setLongOpen(false)}
          components={LONG_BOM}
          fileName="Fasteners-300.xlsx"
          saving={saving}
          onSave={fakeSaveFor(LONG_BOM.length, () => setLongOpen(false))}
        />
        <ExtractedBomModal
          open={viewerOpen}
          onClose={() => setViewerOpen(false)}
          components={BOM_ROWS}
          completeness={BOM_COMPLETENESS}
          fileName="AMR-Drive-BOM-v3.xlsx"
          readOnly
        />
      </Section>

      <BomProposalGallery />
    </>
  );
};

export default CriteriaGallery;
